import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "@emotion/styled";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import Snackbar, { SnackbarCloseReason } from "@mui/material/Snackbar";

import { MainContext } from "./Main";
import {
  getAttributesList,
  getAvailableAttributesList,
  getProfile,
  postSaveAttributesList,
} from "../utils/api";
import { IProfile, IAttributeItem } from "../interfaces";

const AttributeEditor = () => {
  const context = useContext(MainContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [snackOpen, setSnackOpen] = useState<boolean>(false);
  const [snackbarContent, setSnackbarContent] = useState<string>("");
  const [availableAttributesList, setAvailableAttributesList] = useState<
    IAttributeItem[]
  >([]);
  const [idToName, setIdToName] = useState<Record<string, string>>({});
  const [selectedAttributes, setSelectedAttributes] = useState<string[]>([]);
  const [profileId, setProfileId] = useState<string | null>(
    useParams().profileId || null,
  );
  const [profile, setProfile] = useState<IProfile | null>(null);

  useEffect(() => {
    const getAttributeListAsync = async (localProfileId: string) => {
      const attributesListResponse = await getAttributesList(localProfileId);
      if (attributesListResponse.length === 0) {
        setSelectedAttributes([]);
      } else {
        setSelectedAttributes(attributesListResponse);
      }

      const availableResponse = await getAvailableAttributesList(localProfileId);
      const attributes = availableResponse.attributes ?? [];
      setIdToName(availableResponse.idToName ?? {});
      const seenNames = new Set<string>();
      const deduped = attributes.filter((attr: IAttributeItem) => {
        const key = attr.name.toLowerCase();
        if (seenNames.has(key)) return false;
        seenNames.add(key);
        return true;
      });
      const sorted = [...deduped].sort((a: IAttributeItem, b: IAttributeItem) =>
        a.name.localeCompare(b.name),
      );
      setAvailableAttributesList(sorted);
      setLoading(false);
    };

    const getProfileAsync = async (profileId: string) => {
      const profileResponse = await getProfile(profileId);
      setProfile(profileResponse.profile);
    };

    if (profileId === null) {
      if (context.user) {
        setProfileId(context.user.profileId);
        getAttributeListAsync(context.user.profileId);
      } else {
        navigate("/login");
      }
    } else {
      getProfileAsync(profileId);
      getAttributeListAsync(profileId);
    }
  }, [context.user, navigate, profileId]);

  const handleClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackOpen(false);
  };

  const handleSaveClick = async () => {
    setSaving(true);
    try {
      await postSaveAttributesList(selectedAttributes, profileId as string);
      setSnackbarContent("Attributes list saved");
      setSnackOpen(true);
    } catch (e) {
      console.error(e);
      setSnackbarContent("Error saving attributes");
      setSnackOpen(true);
    }
    setSaving(false);
  };

  const handleAddAttribute = (id: string) => {
    setSelectedAttributes((prev) => (prev.includes(id) ? prev : [...prev, id]));
  };

  const handleRemoveAttribute = (id: string) => {
    setSelectedAttributes((prev) => prev.filter((selectedId) => selectedId !== id));
  };

  const getDisplayPrefix = (id: string): string => {
    if (id.startsWith("ticket:")) return "Ticket";
    if (id.startsWith("contact:")) return "Contact";
    if (id.startsWith("company:")) return "Company";
    if (id.startsWith("conversation:")) return "Conversation";
    if (id === "conversation Attachments") return "Conversation";
    return "";
  };

  const getDisplayNameForId = (id: string): string => {
    const found = availableAttributesList.find((attr) => attr.id === id);
    if (found) return found.name;
    const rawName = idToName[id];
    if (rawName) {
      const prefix = getDisplayPrefix(id);
      return prefix ? `${prefix}: ${rawName}` : rawName;
    }
    return id;
  };

  return (
    <Styled>
      {!(
        profile?.asanaMembershipActive ||
        profile?.asanaFreeTrialActive ||
        profile?.mondayMembershipActive ||
        profile?.mondayFreeTrialActive ||
        context?.user?.asanaMembershipActive ||
        context?.user?.asanaFreeTrialActive ||
        context?.user?.mondayMembershipActive ||
        context?.user?.mondayFreeTrialActive
      ) ? (
        <>
          <Typography variant="h3" paragraph>
            This feature requires a paid plan or active free trial
          </Typography>

          <Typography variant="h5" paragraph>
            Upgrade now!
          </Typography>
          <div style={{ display: "flex", gap: "1rem" }}>
            <Button variant="contained" href={`/asana-pricing/${profileId}/`}>
              Upgrade Asana in the Inbox
            </Button>
            <Button
              variant="contained"
              href="https://monday.com/marketplace/listing/10000525/intercom#pricing"
            >
              Upgrade monday in the Inbox
            </Button>
          </div>
        </>
      ) : (
        <>
          <Typography variant="h1">Task Attribute Editor</Typography>
          <Typography variant="body1" paragraph>
            Here you can edit which attributes from Intercom are added to the
            task description in Asana/monday.
          </Typography>
          <Typography variant="body1" paragraph>
            On the left are the attributes you have currently selected to appear
            in task descriptions.
          </Typography>
          <Typography variant="body1" paragraph>
            On the right is a list of all attributes available in your Intercom
            workspace. Just add the ones you want and click save.
          </Typography>

          <Typography variant="body1" paragraph>
            <span className="bold">Tickets note:</span> if you do not see Ticket
            attributes in the list, please reauthorize our app in Intercom by
            clicking{" "}
            <a
              href="https://thackd.com/api/intercom_asana_install"
              target="_blank"
              rel="noreferrer"
            >
              here
            </a>
          </Typography>

          {loading ? (
            <div className="loading-container">
              <CircularProgress />
            </div>
          ) : (
            <div className="content">
              <div className="input-container">
                <Typography variant="h3">Selected Attributes</Typography>
                {selectedAttributes.length === 0 ? (
                  <Typography variant="body1">
                    No attributes selected
                  </Typography>
                ) : (
                  selectedAttributes.map((id) => (
                    <div key={id} className="attribute-item">
                      <Button
                        variant="contained"
                        onClick={() => handleRemoveAttribute(id)}
                      >
                        Remove
                      </Button>
                      <Typography variant="body1">
                        {getDisplayNameForId(id)}
                      </Typography>
                    </div>
                  ))
                )}
                <div className="button-container">
                  <LoadingButton
                    loading={saving}
                    variant="contained"
                    onClick={handleSaveClick}
                  >
                    Save
                  </LoadingButton>
                </div>
              </div>
              <div className="available-attributes-container">
                <Typography variant="h3">Available Attributes</Typography>
                {availableAttributesList.map((attr) => (
                  <div key={attr.id} className="attribute-item">
                    <Button
                      variant="contained"
                      onClick={() => handleAddAttribute(attr.id)}
                    >
                      Add
                    </Button>
                    <Typography variant="body1">{attr.name}</Typography>
                  </div>
                ))}
              </div>
            </div>
          )}
          <Snackbar
            open={snackOpen}
            autoHideDuration={3000}
            onClose={handleClose}
            message={snackbarContent}
          />
        </>
      )}
    </Styled>
  );
};

const Styled = styled.div`
  .MuiTypography-h1 {
    margin-bottom: 1rem;
  }

  .italic {
    font-style: italic;
  }

  .bold {
    font-weight: bold;
  }

  .content {
    display: flex;
    align-items: flex-start;
    width: 100%;
    margin-top: 2rem;

    .MuiTypography-h3 {
      margin-bottom: 1rem;
    }

    .MuiTypography-body1 {
      margin-bottom: 0.3rem;
    }

    .available-attributes-container {
      margin-left: 10rem;
    }

    .attribute-item {
      display: flex;
      align-items: center;
      gap: 1rem;
      margin-bottom: 0.5rem;
    }

    .button-container {
      margin-top: 1rem;
    }
  }

  .loading-container {
    display: flex;
    justify-content: center;
    width: 500px;
    max-width: 100%;
    margin-top: 2rem;
  }
`;

export default AttributeEditor;
